import useAlias from '@alias';
import { Build } from '@mui/icons-material';
import LockIcon from '@mui/icons-material/Lock';
import { Box, IconButton, Tooltip } from '@mui/material';
import { AppDefaultState } from '@src/store/reducers';
import { LockedDeviceInfo } from '@src/types/ControlLockMode';
import { DeviceType } from '@src/types/devices/Device.types';
import { memo, ReactElement, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CommandButtonColumnProps } from './types';

const CommandButtonColumn: React.FC<CommandButtonColumnProps> = ({
  deviceId,
  type,
  openCmdModal,
  disabled,
  disabledByInterlock,
}): ReactElement => {
  const alias = useAlias();
  const controlsLockConfig = useSelector(
    (state: AppDefaultState) => state.config.siteMeta.ui.controlsLockConfig,
  );
  const tooltipTitle = alias(
    `Controls disabled. ${controlsLockConfig?.pointName} is above the threshold of ${controlsLockConfig?.thresholdMax}.`,
  );
  const isButtonDisabled = disabled || disabledByInterlock;
  const lockedControllers = useSelector(
    (state: AppDefaultState) => state.controlsLock.lockedControllers,
  );

  const lockedController = useMemo(
    () =>
      type === DeviceType.UNIT_CONTROLLER
        ? lockedControllers.find(
            (device: LockedDeviceInfo) => device.deviceId === deviceId,
          )
        : undefined,
    [type, lockedControllers, deviceId],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      openCmdModal(deviceId);
    },
    [deviceId, openCmdModal],
  );

  if (lockedController) {
    const { updatedAt, updatedBy, note } = lockedController;
    const lockTooltipTitle = alias(
      `Commands locked by ${updatedBy} on ${updatedAt}. Note: ${note}`,
    );
    return (
      <Tooltip title={lockTooltipTitle} placement='top'>
        <Box p={0}>
          <IconButton onClick={handleClick} disabled={isButtonDisabled}>
            <LockIcon color='error' />
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={disabledByInterlock ? tooltipTitle : ''}
      placement='top'
      disableHoverListener={!disabledByInterlock}
    >
      <Box p={0}>
        <IconButton onClick={handleClick} disabled={isButtonDisabled}>
          <Build />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

CommandButtonColumn.displayName = 'CommandButtonColumn';

export default memo(CommandButtonColumn);
